import { Component, OnDestroy } from '@angular/core';
import { HeaderComponent } from '@components/dashboard/header/header.component';
import { NavComponent } from '@components/dashboard/navigation/nav.component';
import { CommonModule } from '@angular/common';
import { Consumer } from '@interfaces/models.interface';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MomentModule } from 'ngx-moment';
import { Apollo } from 'apollo-angular';
import { AuthStore } from '@store/auth.store';
import { BaseCoreUtils } from '@core/base-core-util';
import { ModuleNames } from '@enums/module-names';
import { USERS_OVERVIEW, USERS_LIST } from '@gql_queries/users/queries';
import { ExcelService } from '@services/excel.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { USERS_DOWNLOAD_REPORT } from '@gql_queries/report_download/queries';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-dashboard-users',
  standalone: true,
  imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule, MomentModule, NgxSpinnerModule],
  templateUrl: './dashboard-users.component.html',
  styleUrl: './dashboard-users.component.css'
})

export class DashboardUsersComponent implements OnDestroy {
  private coreUtils!: BaseCoreUtils;

  constructor(
      private router: Router,
      private excelService: ExcelService,
      private apollo: Apollo,
      private authStore: AuthStore,
      private toastr: ToastrService,
      private spinner: NgxSpinnerService
  ) {
      this.coreUtils = new BaseCoreUtils({
          module: ModuleNames.USERS,
          apollo: this.apollo,
          router: this.router,
          authStore: this.authStore,
          excelService: this.excelService,
      });
  }

  public consumerList: Consumer[] = [];

  public searchKeyword: string = '';
  public currentSelectedPageList: Consumer[] = [];
  public maxPages: number = 0;
  public disableActionBtns: any = {
      next: false,
      prev: false,
  };
  public riderPages: number[] = [];
  public currentPage: number = 1;
  public pageSize: number = 15;

  public consumerTabs: Record<string, any> = {
    all: true,
    w_booking: false,
    in_transit: false,
    completed: false,
    n_booking: false,
  }

  public userOverview: Record<string, any> = {
    totalRegistered: 0,
    totalVerified: 0,
    totalUnVerified: 0,
    rideHailingFourWheelsCount: 0,
    rideHailingTwoWheelsCount: 0,
    expressFourWheelsCount: 0,
    expressTwoWheelsCount: 0,
  };

  public sortOrder: Record<string, any> = {
    asc: true,
    desc: false,
  }

  userSortBy: string = 'dateCreated'; // default sort field
  userSortDirection: string = 'desc';  // default sort direction

  isDownloadingReport = false;
  private spinnerShownAt = 0;
  private readonly minSpinnerDurationMs = 800;

  private searchSubject = new Subject<string>();
  private searchSub!: Subscription;

    ngOnInit(): void {
        this.searchSub = this.searchSubject.pipe(
            debounceTime(400),
            distinctUntilChanged(),
        ).subscribe(keyword => {
            this.currentPage = 1;
            this.maxPages = 0;
            this.riderPages = [];
            this.disableActionBtns = { next: false, prev: false };

            this.fetchUsers('', keyword);
        });

        this.fetchUsers();
    }

    downloadCustomersReport(): void {
        if (this.isDownloadingReport) {
            return;
        }

        this.isDownloadingReport = true;
        this.spinnerShownAt = Date.now();
        this.spinner.show();

        this.apollo.query({
            fetchPolicy: 'network-only',
            query: USERS_DOWNLOAD_REPORT,
            variables: {},
        }).pipe(
            finalize(() => this.hideReportSpinner())
        ).subscribe({
            next: (result: any) => {
                const msg: string | undefined = result?.data?.usersReport?.message;

                if (msg) {
                    this.toastr.success(msg, 'User Report', {
                        closeButton: true,
                        timeOut: 3000,
                    });
                } else {
                    this.toastr.error('An error occured, please try again', 'UNKNWN_ERR', {
                        closeButton: true,
                        timeOut: 3000,
                    });
                }
            },
            error: (error) => {
                if (error.message === 'Unauthorized' || error?.networkError?.status === 403) {
                    this.authStore.logout().subscribe(() => {
                        this.router.navigate(['/admin/login']);
                    });
                    return;
                }

                this.toastr.error(error.message || 'Unable to download report', error.code || 'ERR', {
                    closeButton: true,
                    timeOut: 3000,
                });
            },
        });
    }

    private hideReportSpinner(): void {
        const elapsed = Date.now() - this.spinnerShownAt;
        const remainingDelay = Math.max(0, this.minSpinnerDurationMs - elapsed);

        setTimeout(() => {
            this.isDownloadingReport = false;
            this.spinner.hide();
        }, remainingDelay);
    }

    fetchOverview(){
        this.coreUtils.overview(USERS_OVERVIEW, {}, (data) => {
          if(data['usersOverview'] != null){
            var completeInstance = data['usersOverview'];
            Object.keys(this.userOverview).forEach((x) => {
                this.userOverview[x] = completeInstance[x];
            });
          }
        });
    }//end fn

    nextPrev(action: string){
        switch(action){
            case 'next':
                this.fetchUsers('next');
            break;

            case 'prev':
                this.fetchUsers('prev');
            break;
        }//end swithc
    }//end fn


    searchData(event: Event) {
        const inputElement = event.target as HTMLInputElement;
        this.searchKeyword = inputElement.value;
        this.searchSubject.next(this.searchKeyword);
    }//end nfn

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
    }

    viewDetails(details: Consumer){
        this.router.navigate(['/admin/users', details.userId]);
    }//end fn

    filterConsumers(tabindex: string){
        this.setSelectedPageDataBasedOnStatus(tabindex);
    }//end

    setSelectedPageDataBasedOnStatus(tabindex: string){
        Object.keys(this.consumerTabs).forEach((x) => {
            this.consumerTabs[x] = false;
        });

        this.consumerTabs[tabindex] = true;
    }//end fn

    setSortOrder(order: string){
        Object.keys(this.sortOrder).forEach((x) => {
            this.sortOrder[x] = false;
        });

        this.sortOrder[order] = true;
    }//end fn

    fetchUsers(action: string = '', searchQuery: string = '') {
        var fromPage = this.currentPage;

        if(action != ''){
            switch(action){
                case 'next':
                    this.currentPage++
                break;

                case 'prev':
                    if(this.currentPage != 1){
                        this.currentPage--
                    }//end if
                break;
            }//end swithc
        }//end if

        this.coreUtils.fetchList(USERS_LIST, {
            page: this.currentPage,
            searchQuery: searchQuery,
            sortBy: this.userSortBy,
            sortDirection: this.userSortDirection,
        }, (data) => {
            if (data['users']?.length !== 0) {
                this.consumerList = data['users'];
                this.currentSelectedPageList = this.consumerList;
            } else {
                this.currentPage = fromPage;
            }
            this.fetchOverview();
        });
    }

    onSortChange(){
        this.fetchUsers();
    }
}
